import type { FunctionalComponent } from 'preact';
import { useRef, useState, useCallback, useEffect, useMemo } from 'preact/hooks';
import { useStore } from '../store';
import { verifySponsorDiscount, createSponsorView } from '../api/client';
import { formatCurrency } from '../utils/theme';
import { allInListPriceCentsForTier, estimateSponsorDiscountedCentsForTier } from '../lib/tierCartPricing';
import {
    buildTierSponsorPreRollVideos,
    buildSponsorLeadPromptQueue,
    type TierSponsorPreRollVideo,
} from '../lib/tierLinkedSponsors';
import type { FeeStructure } from '../types';
import { SponsorLeadConsentPanel } from './SponsorLeadConsent';

/** Inline SVG play icon - baked in for consistent rendering across all environments (iOS WebView, Android, browsers) */
const PlayIconSvg = ({ size = 24, className = '' }: { size?: number; className?: string }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" class={className} aria-hidden="true">
        <path d="M8 5v14l11-7z" />
    </svg>
);

/** Inline SVG pause icon */
const PauseIconSvg = ({ size = 24, className = '' }: { size?: number; className?: string }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" class={className} aria-hidden="true">
        <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
    </svg>
);

/** Inline SVG fullscreen expand icon */
const FullscreenIconSvg = ({ size = 20, className = '' }: { size?: number; className?: string }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" class={className} aria-hidden="true">
        <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" />
    </svg>
);

/** Inline SVG exit fullscreen icon */
const ExitFullscreenIconSvg = ({ size = 20, className = '' }: { size?: number; className?: string }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" class={className} aria-hidden="true">
        <path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 8h2v-3h3v-2h-5v5zm2-8V5h-2v5h5V8h-3z" />
    </svg>
);

function recordPreRollStepEnd(
    dispatch: ReturnType<typeof useStore>['dispatch'],
    eventId: string | undefined,
    correlationIdRef: { current: string | null },
    video: TierSponsorPreRollVideo,
    durationWatched: number,
) {
    if (!correlationIdRef.current) {
        correlationIdRef.current = crypto.randomUUID();
        dispatch({ type: 'SET_VIEW_CORRELATION_ID', correlationId: correlationIdRef.current });
    }
    if (video.eventSponsorId) {
        dispatch({ type: 'ADD_WATCHED_SPONSOR_ATTRIBUTION', event_sponsor_id: video.eventSponsorId });
    }
    const viewSponsorId = video.viewSponsorId;
    if (viewSponsorId && eventId) {
        createSponsorView({
            sponsor: viewSponsorId,
            event: eventId,
            duration_watched: durationWatched,
            view_type: 'sponsor',
            correlation_id: correlationIdRef.current,
        }).catch(() => {});
    }
}

function queueSponsorLeadPrompts(
    dispatch: ReturnType<typeof useStore>['dispatch'],
    preRollVideos: TierSponsorPreRollVideo[],
    fallback?: { eventSponsorId: string; sponsorName: string },
) {
    const prompts = buildSponsorLeadPromptQueue(preRollVideos, fallback);
    if (prompts.length > 0) {
        dispatch({ type: 'START_SPONSOR_LEAD_QUEUE', prompts });
    }
}

/**
 * Fullscreen video modal overlay for free sponsored tiers.
 * Matches the app's design: opens over the screen, plays the sponsor video,
 * auto-applies discount on completion, then dismisses.
 */
export const SponsorVideoOverlay: FunctionalComponent = () => {
    const { state, dispatch } = useStore();
    const videoRef = useRef<HTMLVideoElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const correlationIdRef = useRef<string | null>(null);
    const [videoIndex, setVideoIndex] = useState(0);
    const [progress, setProgress] = useState(0);
    const [paused, setPaused] = useState(true);
    const [applying, setApplying] = useState(false);
    const [applied, setApplied] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const appliedRef = useRef(false);
    const playbackStartedRef = useRef(false);
    const [modalVideoAspect, setModalVideoAspect] = useState<string | null>(null);

    const tier = state.feeStructures.find(f => f._id === state.sponsorVideoTierId);
    const sponsorDiscount = tier?.sponsor_discount;
    const preRollVideos = useMemo(
        () => (tier ? buildTierSponsorPreRollVideos(tier, state.sponsors) : []),
        [tier, state.sponsors],
    );
    const currentVideo = preRollVideos[videoIndex];
    const sponsor = tier?.sponsor;

    const dismiss = useCallback(() => {
        dispatch({ type: 'SET_SPONSOR_VIDEO_TIER', tierId: null });
    }, [dispatch]);

    const finishAllVideos = useCallback(async () => {
        if (appliedRef.current || !state.event || !tier) return;
        appliedRef.current = true;

        const fallback =
            tier.sponsor?._id && (tier.sponsor?.sponsor as { name?: string } | undefined)?.name
                ? {
                      eventSponsorId: tier.sponsor._id,
                      sponsorName:
                          (tier.sponsor.sponsor as { name?: string }).name ||
                          currentVideo?.sponsorName ||
                          'Sponsor',
                  }
                : currentVideo?.eventSponsorId
                  ? { eventSponsorId: currentVideo.eventSponsorId, sponsorName: currentVideo.sponsorName }
                  : undefined;
        queueSponsorLeadPrompts(dispatch, preRollVideos, fallback);

        if (tier.sponsur_free_tier && !sponsorDiscount) {
            dispatch({
                type: 'UNLOCK_SPONSOR_TIER',
                tierId: tier._id,
                value: 100,
                discountType: 'percentage',
                code: '',
            });
            setApplied(true);
            return;
        }

        if (!sponsorDiscount) return;
        setApplying(true);
        try {
            const result = await verifySponsorDiscount(state.event._id, sponsorDiscount._id);
            dispatch({
                type: 'UNLOCK_SPONSOR_TIER',
                tierId: tier._id,
                value: sponsorDiscount.discount,
                discountType: sponsorDiscount.discount_type,
                code: result.code,
            });
            setApplied(true);
        } catch (e: any) {
            setError(e.message || 'Could not verify discount');
            appliedRef.current = false;
        } finally {
            setApplying(false);
        }
    }, [currentVideo, dispatch, preRollVideos, sponsorDiscount, state.event, tier]);

    const handleTimeUpdate = useCallback(() => {
        const video = videoRef.current;
        if (!video || video.duration === 0) return;
        setProgress((video.currentTime / video.duration) * 100);
    }, []);

    const handleVideoEnd = useCallback(() => {
        setProgress(100);

        const video = currentVideo;
        if (!video) return;

        recordPreRollStepEnd(
            dispatch,
            state.event?._id,
            correlationIdRef,
            video,
            videoRef.current?.duration || 0,
        );

        if (videoIndex + 1 < preRollVideos.length) {
            setVideoIndex(i => i + 1);
            if (playbackStartedRef.current) {
                setPaused(false);
            }
            return;
        }

        setPaused(true);
        finishAllVideos();
    }, [currentVideo, dispatch, finishAllVideos, preRollVideos.length, state.event?._id, videoIndex]);

    const handlePlayPause = useCallback(() => {
        const video = videoRef.current;
        if (!video) return;
        if (video.paused) {
            video
                .play()
                .then(() => setPaused(false))
                .catch(() => {});
        } else {
            video.pause();
            setPaused(true);
        }
    }, []);

    const toggleFullscreen = useCallback(() => {
        const video = videoRef.current;
        if (!video) return;
        if (document.fullscreenElement) {
            document.exitFullscreen().catch(() => {});
            setIsFullscreen(false);
        } else {
            const el = containerRef.current || video;
            if (el.requestFullscreen) {
                el.requestFullscreen()
                    .then(() => setIsFullscreen(true))
                    .catch(() => {
                        if ((video as any).webkitEnterFullscreen) {
                            (video as any).webkitEnterFullscreen();
                            setIsFullscreen(true);
                        }
                    });
            } else if ((video as any).webkitEnterFullscreen) {
                (video as any).webkitEnterFullscreen();
                setIsFullscreen(true);
            }
        }
    }, []);

    useEffect(() => {
        if (applied && state.sponsorLeadQueue.length === 0) {
            const timer = setTimeout(dismiss, 1800);
            return () => clearTimeout(timer);
        }
    }, [applied, dismiss, state.sponsorLeadQueue.length]);

    useEffect(() => {
        const handler = () => setIsFullscreen(!!document.fullscreenElement);
        document.addEventListener('fullscreenchange', handler);
        return () => document.removeEventListener('fullscreenchange', handler);
    }, []);

    // Reset state when a new tier is selected
    useEffect(() => {
        if (state.sponsorVideoTierId) {
            setVideoIndex(0);
            setProgress(0);
            setPaused(true);
            setApplying(false);
            setApplied(false);
            setError(null);
            appliedRef.current = false;
            correlationIdRef.current = null;
            playbackStartedRef.current = false;
            setModalVideoAspect(null);
        }
    }, [state.sponsorVideoTierId]);

    // Auto-play when advancing to the next video in the queue
    useEffect(() => {
        const video = videoRef.current;
        if (!video || !currentVideo?.url) return;
        if (videoIndex === 0 && !playbackStartedRef.current) return;
        setProgress(0);
        setModalVideoAspect(null);
        video.load();
        video
            .play()
            .then(() => {
                playbackStartedRef.current = true;
                setPaused(false);
            })
            .catch(() => setPaused(true));
    }, [videoIndex, currentVideo?.url]);

    const showLeadPanel = state.sponsorLeadQueue.length > 0;
    const showPlayOverlay =
        paused && !(playbackStartedRef.current && videoIndex > 0) && !showLeadPanel;

    const handleModalVideoMetadata = useCallback((e: Event) => {
        const v = e.currentTarget as HTMLVideoElement;
        if (v.videoWidth > 0 && v.videoHeight > 0) {
            setModalVideoAspect(`${v.videoWidth} / ${v.videoHeight}`);
        }
    }, []);

    if (!state.sponsorVideoTierId || !tier || preRollVideos.length === 0 || !currentVideo) return null;
    const isSponsorFreeTier = !!tier.sponsur_free_tier;
    if (!sponsorDiscount && !isSponsorFreeTier) return null;

    const currency = tier?.currency || 'USD';
    const sponsorName = currentVideo.sponsorName || sponsor?.sponsor?.name || 'Sponsor';
    const adTotal = preRollVideos.length;
    const adIndex = videoIndex + 1;
    const isFree = isSponsorFreeTier || (sponsorDiscount && sponsorDiscount.discount >= 100);
    const discountLabel = isFree ? 'Free' : sponsorDiscount ? `${sponsorDiscount.discount}% Off` : 'Free';
    const allInListCents = allInListPriceCentsForTier(tier);
    const discountedPrice = isFree
        ? 0
        : sponsorDiscount
          ? estimateSponsorDiscountedCentsForTier(tier, sponsorDiscount)
          : allInListCents;

    return (
        <div class="mw-video-overlay" onClick={dismiss}>
            <div class="mw-sponsor-modal" onClick={e => e.stopPropagation()}>
                <button type="button" class="mw-sponsor-close" onClick={dismiss} aria-label="Close">
                    ×
                </button>

                <div
                    class="mw-sponsor-video-area"
                    ref={containerRef}
                    style={modalVideoAspect != null ? { aspectRatio: modalVideoAspect } : { aspectRatio: '16 / 9' }}>
                    <video
                        key={`${currentVideo.url}-${videoIndex}`}
                        ref={videoRef}
                        src={currentVideo.url}
                        poster={currentVideo.thumbnail}
                        onTimeUpdate={handleTimeUpdate}
                        onLoadedMetadata={handleModalVideoMetadata}
                        onEnded={handleVideoEnd}
                        onPlay={() => {
                            playbackStartedRef.current = true;
                            setPaused(false);
                        }}
                        onPause={() => setPaused(true)}
                        onError={() => setError('Video could not be loaded.')}
                        controls={false}
                        autoPlay
                        playsInline
                        preload="auto"
                    />
                    <div class="mw-sponsor-ad-badge">
                        {adTotal > 1 ? `AD ${adIndex} of ${adTotal}` : 'AD'}
                    </div>
                    <div
                        class={`mw-sponsor-play-overlay${showPlayOverlay ? ' mw-sponsor-play-overlay--visible' : ''}`}
                        onClick={handlePlayPause}>
                        <div class="mw-sponsor-play-btn">
                            {paused ? <PlayIconSvg size={28} /> : <PauseIconSvg size={28} />}
                        </div>
                    </div>
                    {showLeadPanel && <SponsorLeadConsentPanel />}
                    <button
                        type="button"
                        class="mw-sponsor-fullscreen-btn"
                        onClick={toggleFullscreen}
                        aria-label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
                        {isFullscreen ? <ExitFullscreenIconSvg size={18} /> : <FullscreenIconSvg size={18} />}
                    </button>
                </div>

                <div class="mw-sponsor-progress">
                    <div class="mw-sponsor-progress-fill" style={{ width: `${progress}%` }} />
                </div>

                <div class="mw-sponsor-info">
                    <div class="mw-sponsor-header">
                        <span class="mw-sponsor-limited-badge">LIMITED TIME</span>
                        <div class="mw-sponsor-price">
                            <span class="mw-sponsor-price-original">{formatCurrency(allInListCents, currency)}</span>
                            <span class="mw-sponsor-price-free">
                                {isFree ? 'Free' : formatCurrency(discountedPrice, currency)}
                            </span>
                        </div>
                    </div>

                    <div class="mw-sponsor-title">Sponsored by {sponsorName}</div>

                    <p class="mw-sponsor-desc">
                        {isFree ? (
                            <span>
                                Watch a short clip from <strong>{sponsorName}</strong> to unlock your free{' '}
                                <strong>{tier.fee_name}</strong> ticket.
                            </span>
                        ) : (
                            <span>
                                Watch a short clip from <strong>{sponsorName}</strong> to unlock{' '}
                                <strong>{sponsorDiscount?.discount ?? 0}%</strong> off your{' '}
                                <strong>{tier.fee_name}</strong> ticket.
                            </span>
                        )}
                    </p>

                    {applying && (
                        <div class="mw-sponsor-status">
                            <div class="mw-spinner" style={{ width: '18px', height: '18px' }} />
                            <span>Applying discount...</span>
                        </div>
                    )}
                    {applied && (
                        <div class="mw-sponsor-applied">
                            <div class="mw-sponsor-check">✓</div>
                            <span>{discountLabel} applied!</span>
                        </div>
                    )}
                    {error && (
                        <div class="mw-sponsor-error">
                            <p>{error}</p>
                            <button type="button" class="mw-btn mw-btn--outline mw-btn--sm" onClick={dismiss}>
                                Continue without discount
                            </button>
                        </div>
                    )}
                    {!applied && !applying && !error && (
                        <button type="button" class="mw-sponsor-skip" onClick={dismiss}>
                            Skip
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

/**
 * Thumbnail card for free sponsored tiers on the ticket selection screen.
 * Shows video poster, play button, AD badge, tier info, and "Watch to Unlock" button.
 * Tapping opens the SponsorVideoOverlay modal.
 */
export const SponsorThumbnailCard: FunctionalComponent<{
    tier: FeeStructure;
    unlocked: boolean;
}> = ({ tier, unlocked }) => {
    const { dispatch, state } = useStore();
    const preRollVideos = useMemo(
        () => buildTierSponsorPreRollVideos(tier, state.sponsors),
        [tier, state.sponsors],
    );
    const firstVideo = preRollVideos[0];
    const sponsor = tier.sponsor;
    if (preRollVideos.length === 0) return null;
    if (!sponsor || (!tier.sponsor_discount && !tier.sponsur_free_tier)) return null;

    const currency = tier.currency || 'USD';
    const sponsorName = firstVideo.sponsorName || sponsor?.sponsor?.name || 'Sponsor';

    const openVideo = () => {
        dispatch({ type: 'SET_SPONSOR_VIDEO_TIER', tierId: tier._id });
    };

    return (
        <div class="mw-sponsor-thumb-card">
            {/* Video thumbnail area */}
            <div class="mw-sponsor-thumb-area" onClick={!unlocked ? openVideo : undefined}>
                {firstVideo.thumbnail ? (
                    <img src={firstVideo.thumbnail} alt="" class="mw-sponsor-thumb-img" />
                ) : (
                    <div class="mw-sponsor-thumb-placeholder">
                        <PlayIconSvg size={48} className="mw-sponsor-thumb-placeholder-icon" />
                    </div>
                )}
                {!unlocked && (
                    <div class="mw-sponsor-thumb-overlay">
                        <div class="mw-inline-player-play-btn">
                            <PlayIconSvg size={32} />
                        </div>
                    </div>
                )}
                <div class="mw-sponsor-thumb-badge">AD</div>
            </div>

            {/* Content */}
            <div class="mw-sponsor-thumb-body">
                <div class="mw-sponsor-thumb-header">
                    <span class="mw-sponsor-limited-badge">LIMITED TIME</span>
                    <div class="mw-sponsor-price">
                        <span class="mw-sponsor-price-original">
                            {formatCurrency(allInListPriceCentsForTier(tier), currency)}
                        </span>
                        <span class="mw-sponsor-price-free">Free</span>
                    </div>
                </div>
                <div style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: '0.25rem' }}>Sponsored Ticket</div>
                <p
                    style={{
                        fontSize: '0.8rem',
                        color: 'var(--mw-text-muted)',
                        margin: '0 0 0.75rem',
                        lineHeight: 1.4,
                    }}>
                    Watch a short clip from <strong>{sponsorName}</strong> to unlock your free{' '}
                    <strong>{tier.fee_name}</strong> ticket.
                </p>
                {unlocked ? (
                    <div class="mw-sponsor-card-unlocked">
                        <span class="mw-sponsor-check-sm">✓</span> Free ticket unlocked
                    </div>
                ) : (
                    <button type="button" class="mw-sponsor-watch-btn" onClick={openVideo}>
                        <span class="mw-sponsor-watch-icon">🔒</span>
                        Watch to Unlock
                    </button>
                )}
            </div>
        </div>
    );
};

/**
 * Inline video player for paid sponsored tiers on the checkout/payment screen.
 * Plays directly in-place; on completion, applies the per-tier discount.
 */
export const InlineSponsorPlayer: FunctionalComponent<{
    tier: FeeStructure;
    compact?: boolean;
}> = ({ tier, compact }) => {
    const { state, dispatch } = useStore();
    const videoRef = useRef<HTMLVideoElement>(null);
    const correlationIdRef = useRef<string | null>(null);
    const [videoIndex, setVideoIndex] = useState(0);
    const [progress, setProgress] = useState(0);
    const [paused, setPaused] = useState(true);
    const [started, setStarted] = useState(false);
    const [applying, setApplying] = useState(false);
    const [applied, setApplied] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const appliedRef = useRef(false);
    const playbackStartedRef = useRef(false);
    const [videoAspect, setVideoAspect] = useState<string | null>(null);
    const [videoLayout, setVideoLayout] = useState<'unknown' | 'portrait' | 'landscape'>('unknown');

    const preRollVideos = useMemo(
        () => buildTierSponsorPreRollVideos(tier, state.sponsors),
        [tier, state.sponsors],
    );
    const currentVideo = preRollVideos[videoIndex];
    const sponsor = tier.sponsor;
    const sponsorDiscount = tier.sponsor_discount;
    const unlocked = !!state.unlockedSponsorTiers[tier._id];

    const currency = tier.currency || 'USD';
    const sponsorName = currentVideo?.sponsorName || sponsor?.sponsor?.name || 'Sponsor';
    const allInListCents = allInListPriceCentsForTier(tier);
    const discountedPrice = estimateSponsorDiscountedCentsForTier(tier, sponsorDiscount);

    const finishAllVideos = useCallback(async () => {
        if (appliedRef.current || !sponsorDiscount || !state.event) return;
        appliedRef.current = true;

        const fallback =
            tier.sponsor?._id
                ? {
                      eventSponsorId: tier.sponsor._id,
                      sponsorName: sponsorName,
                  }
                : undefined;
        queueSponsorLeadPrompts(dispatch, preRollVideos, fallback);

        setApplying(true);
        try {
            const result = await verifySponsorDiscount(state.event._id, sponsorDiscount._id);
            dispatch({
                type: 'UNLOCK_SPONSOR_TIER',
                tierId: tier._id,
                value: sponsorDiscount.discount,
                discountType: sponsorDiscount.discount_type,
                code: result.code,
            });
            setApplied(true);
        } catch (e: any) {
            setError(e.message || 'Could not verify discount');
            appliedRef.current = false;
        } finally {
            setApplying(false);
        }
    }, [dispatch, preRollVideos, sponsorDiscount, sponsorName, state.event, tier._id, tier.sponsor?._id]);

    const handleTimeUpdate = useCallback(() => {
        const video = videoRef.current;
        if (!video || video.duration === 0) return;
        setProgress((video.currentTime / video.duration) * 100);
    }, []);

    const handleInlineVideoMetadata = useCallback((e: Event) => {
        const v = e.currentTarget as HTMLVideoElement;
        const w = v.videoWidth;
        const h = v.videoHeight;
        if (w > 0 && h > 0) {
            setVideoAspect(`${w} / ${h}`);
            setVideoLayout(h > w ? 'portrait' : 'landscape');
        }
    }, []);

    const handleVideoEnd = useCallback(() => {
        setProgress(100);

        const video = currentVideo;
        if (!video) return;

        recordPreRollStepEnd(
            dispatch,
            state.event?._id,
            correlationIdRef,
            video,
            videoRef.current?.duration || 0,
        );

        if (videoIndex + 1 < preRollVideos.length) {
            setVideoIndex(i => i + 1);
            if (playbackStartedRef.current) {
                setPaused(false);
                setStarted(true);
            }
            return;
        }

        setPaused(true);
        finishAllVideos();
    }, [currentVideo, dispatch, finishAllVideos, preRollVideos.length, state.event?._id, videoIndex]);

    useEffect(() => {
        const video = videoRef.current;
        if (!video || !currentVideo?.url || videoIndex === 0) return;
        setProgress(0);
        setVideoAspect(null);
        setVideoLayout('unknown');
        video.load();
        video
            .play()
            .then(() => {
                playbackStartedRef.current = true;
                setPaused(false);
                setStarted(true);
            })
            .catch(() => setPaused(true));
    }, [videoIndex, currentVideo?.url]);

    const handlePlayPause = useCallback(() => {
        const video = videoRef.current;
        if (!video) return;
        if (video.paused) {
            video
                .play()
                .then(() => {
                    playbackStartedRef.current = true;
                    setPaused(false);
                    setStarted(true);
                })
                .catch(() => {});
        } else {
            video.pause();
            setPaused(true);
        }
    }, []);

    if (!sponsor || !sponsorDiscount || preRollVideos.length === 0 || !currentVideo) return null;

    const adTotal = preRollVideos.length;
    const adIndex = videoIndex + 1;
    const showLeadPanel = state.sponsorLeadQueue.length > 0;
    const showDoneState = (unlocked || applied) && !showLeadPanel;

    if (showDoneState) {
        return (
            <div class="mw-inline-player mw-inline-player--done">
                <div class="mw-inline-player-done">
                    <span class="mw-sponsor-check-sm">✓</span>
                    <span>
                        {sponsorDiscount.discount}% Off unlocked — save{' '}
                        {formatCurrency(allInListCents - discountedPrice, currency)} on {tier.fee_name}
                    </span>
                </div>
            </div>
        );
    }

    return (
        <div class={`mw-inline-player${compact ? ' mw-inline-player--compact' : ''}`}>
            <div class="mw-inline-player-header">
                <div>
                    <span
                        class="mw-sponsor-ad-badge"
                        style={{ position: 'static', fontSize: '0.6rem', padding: '0.1rem 0.35rem' }}>
                        {adTotal > 1 ? `AD ${adIndex}/${adTotal}` : 'AD'}
                    </span>
                    <span class="mw-inline-player-sponsor">Sponsored by {sponsorName}</span>
                </div>
                <div class="mw-inline-player-reward">
                    <span class="mw-sponsor-price-original" style={{ fontSize: '0.75rem' }}>
                        {formatCurrency(allInListCents, currency)}
                    </span>
                    <span class="mw-sponsor-price-free" style={{ fontSize: '0.85rem' }}>
                        {formatCurrency(discountedPrice, currency)}
                    </span>
                </div>
            </div>

            <div
                class={[
                    'mw-inline-player-video',
                    videoLayout === 'portrait' && 'mw-inline-player-video--portrait',
                    videoLayout === 'landscape' && 'mw-inline-player-video--landscape',
                ]
                    .filter(Boolean)
                    .join(' ')}
                style={videoAspect != null ? { aspectRatio: videoAspect } : undefined}
                onClick={handlePlayPause}>
                <video
                    key={`${currentVideo.url}-${videoIndex}`}
                    ref={videoRef}
                    src={currentVideo.url}
                    poster={currentVideo.thumbnail}
                    onTimeUpdate={handleTimeUpdate}
                    onLoadedMetadata={handleInlineVideoMetadata}
                    onEnded={handleVideoEnd}
                    onPlay={() => {
                        playbackStartedRef.current = true;
                        setPaused(false);
                    }}
                    onPause={() => setPaused(true)}
                    onError={() => setError('Video could not be loaded.')}
                    controls={false}
                    playsInline
                    preload="metadata"
                />
                {(paused || !started) &&
                    !(playbackStartedRef.current && videoIndex > 0) &&
                    !showLeadPanel && (
                    <div class="mw-inline-player-play-overlay">
                        <div class="mw-inline-player-play-btn">
                            <PlayIconSvg size={32} />
                        </div>
                        {!started && (
                            <div class="mw-inline-player-cta">
                                Watch to save {formatCurrency(allInListCents - discountedPrice, currency)}
                            </div>
                        )}
                    </div>
                )}
                {showLeadPanel && <SponsorLeadConsentPanel />}
            </div>

            {started && (
                <div class="mw-sponsor-progress" style={{ borderRadius: 0 }}>
                    <div class="mw-sponsor-progress-fill" style={{ width: `${progress}%` }} />
                </div>
            )}

            {applying && (
                <div class="mw-inline-player-status">
                    <div class="mw-spinner" style={{ width: '16px', height: '16px' }} />
                    <span>Applying discount...</span>
                </div>
            )}
            {error && (
                <div class="mw-inline-player-status" style={{ color: 'var(--mw-error)' }}>
                    {error}
                </div>
            )}

            <div class="mw-inline-player-desc">
                Watch a short clip to save <strong>{formatCurrency(allInListCents - discountedPrice, currency)}</strong>{' '}
                on{' '}
                <strong>{tier.fee_name}</strong>.
            </div>
        </div>
    );
};
